// src/components/MapView/Header.jsx
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MapPin,
  ChevronDown,
  FileText,
  HelpCircle,
  LogOut,
  Settings,
  User,
  RefreshCw,
  Users,
  Briefcase,
  Loader2,
} from "lucide-react";

import {
  useClientConfig,
  useMapSelector,
  useFeatures,
  useMapData,
  useAvatars,
  useReports,
} from "../../hooks";
import { useCompetitorData, useOpportunityData } from "../../hooks/useData";
import {
  signOutUser,
  selectUser,
  selectIsAdmin,
  selectClientAccess,
} from "../../store/authSlice";
import { selectClientSlug } from "../../store/configSlice";
import { clearData } from "../../store/dataSlice";
import { toggleCompetitors, toggleOpportunities } from "../../store/mapSlice";

/**
 * Convert a URL slug to a readable display name.
 * e.g., "queen-city-laundry" => "Queen City Laundry"
 */
const formatSlugAsName = (slug) =>
  slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/**
 * Header component with branding and map selector.
 *
 * Mobile (<lg): two rows.
 *   Row 1 — Logo, map selector, user menu.
 *   Row 2 — Feature toggles (competitors, opportunities) and nav links (Reports, FAQ).
 *
 * Desktop (lg+): single row with all items inline.
 */
const Header = () => {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const [mapSelectorOpen, setMapSelectorOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [clientSwitcherOpen, setClientSwitcherOpen] = useState(false);
  const [mobileDropdownTop, setMobileDropdownTop] = useState(56);

  const user = useSelector(selectUser);
  const isAdmin = useSelector(selectIsAdmin);
  const clientSlug = useSelector(selectClientSlug);
  const clientAccess = useSelector(selectClientAccess);

  const { clientInfo } = useClientConfig();
  const { activeMap, availableMaps, mapsByGeoType, switchMap } =
    useMapSelector();
  const { reportsEnabled, competitorsEnabled, opportunitiesEnabled } =
    useFeatures();
  const { reload } = useMapData();
  const { avatars } = useAvatars();
  const { globalReports } = useReports();
  const {
    visible: competitorsVisible,
    loading: competitorsLoading,
    hasCompetitorsPath,
  } = useCompetitorData();
  const {
    visible: opportunitiesVisible,
    loading: opportunitiesLoading,
    hasOpportunitiesPath,
  } = useOpportunityData();

  const showCompetitorsToggle = competitorsEnabled && hasCompetitorsPath;
  const showOpportunitiesToggle = opportunitiesEnabled && hasOpportunitiesPath;

  // Client switcher: show dropdown when user has access to 2+ clients
  const hasMultipleClients = clientAccess.length > 1;

  // Display name for the active client (use loaded config name, fall back to slug formatting)
  const activeClientDisplayName =
    clientInfo?.name || (clientSlug ? formatSlugAsName(clientSlug) : "");

  // Build list of other accessible clients for the switcher dropdown
  const otherClients = useMemo(() => {
    if (!hasMultipleClients) return [];
    return clientAccess
      .filter((slug) => slug !== clientSlug)
      .map((slug) => ({ slug, name: formatSlugAsName(slug) }));
  }, [clientAccess, clientSlug, hasMultipleClients]);

  const avatarLookup = useMemo(() => {
    const lookup = {};
    avatars.forEach((a) => {
      lookup[a.id] = a;
    });
    return lookup;
  }, [avatars]);

  const hasFaq = globalReports.some(
    (r) => r.type === "markdown" || r.id === "faq",
  );

  // Whether the mobile action bar (second row) should render
  const showMobileActionBar =
    showCompetitorsToggle ||
    showOpportunitiesToggle ||
    (reportsEnabled && !!clientSlug) ||
    (hasFaq && !!clientSlug);

  // Position mobile map dropdown just below the header.
  // Accounts for the action bar making the header taller on mobile.
  useEffect(() => {
    if (mapSelectorOpen && headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      setMobileDropdownTop(rect.bottom + 4);
    }
  }, [mapSelectorOpen]);

  const getMapDescription = (map) => {
    if (map.avatarId && avatarLookup[map.avatarId]) {
      return avatarLookup[map.avatarId].description;
    }
    if (map.scoringAlgorithm?.description) {
      return map.scoringAlgorithm.description;
    }
    return null;
  };

  const handleMapSelect = (mapId) => {
    if (mapId !== activeMap?.id) {
      dispatch(clearData());
      switchMap(mapId);
    }
    setMapSelectorOpen(false);
  };

  const handleSignOut = () => {
    dispatch(signOutUser());
  };

  const handleReload = () => {
    reload();
  };

  const handleToggleCompetitors = () => {
    dispatch(toggleCompetitors());
  };

  const handleToggleOpportunities = () => {
    dispatch(toggleOpportunities());
  };

  const hasMultipleMaps = availableMaps.length > 1;
  const hasZCTA = mapsByGeoType.ZCTA.length > 0;
  const hasBG = mapsByGeoType.BG.length > 0;

  /**
   * Renders the map selector dropdown items grouped by geography type.
   * Shared between the mobile (fixed) and desktop (absolute) dropdowns.
   */
  const renderMapOptions = () => (
    <>
      {hasZCTA && (
        <div>
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            ZIP Code (ZCTA)
          </div>
          {mapsByGeoType.ZCTA.map((map) => (
            <button
              key={map.id}
              onClick={() => handleMapSelect(map.id)}
              className={`w-full text-left px-4 py-2.5 text-sm transition ${
                map.id === activeMap?.id
                  ? "bg-secondary text-white"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              }`}
            >
              <div className="font-medium">{map.label}</div>
              {getMapDescription(map) && (
                <div
                  className={`text-xs ${
                    map.id === activeMap?.id ? "text-green-100" : "text-gray-500"
                  }`}
                >
                  {getMapDescription(map)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {hasBG && (
        <div>
          <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t">
            Block Group
          </div>
          {mapsByGeoType.BG.map((map) => (
            <button
              key={map.id}
              onClick={() => handleMapSelect(map.id)}
              className={`w-full text-left px-4 py-2.5 text-sm transition ${
                map.id === activeMap?.id
                  ? "bg-secondary text-white"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              }`}
            >
              <div className="font-medium">{map.label}</div>
              {getMapDescription(map) && (
                <div
                  className={`text-xs ${
                    map.id === activeMap?.id ? "text-green-100" : "text-gray-500"
                  }`}
                >
                  {getMapDescription(map)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <header
      ref={headerRef}
      className="bg-primary text-white shadow-md z-[1001] relative flex-shrink-0"
    >
      <div className="px-3 py-2 lg:px-4 lg:py-3">
        {/* ============================================
            PRIMARY ROW
            Mobile: Logo + Map Selector + User Menu
            Desktop: all items inline
            ============================================ */}
        <div className="flex items-center justify-between gap-2">
          {/* Logo and title */}
          <div className="flex items-center gap-2 lg:gap-3 min-w-0">
            <MapPin className="text-secondary flex-shrink-0" size={24} />
            <div className="min-w-0">
              <h1 className="text-sm lg:text-lg font-bold leading-tight truncate">
                {clientInfo?.projectName || "Heat Map"}
              </h1>

              {/* Client name / switcher (desktop only) */}
              {hasMultipleClients ? (
                <div className="relative hidden lg:block">
                  <button
                    onClick={() => setClientSwitcherOpen(!clientSwitcherOpen)}
                    className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition truncate"
                  >
                    <span className="truncate">{activeClientDisplayName}</span>
                    <ChevronDown
                      size={12}
                      className={`flex-shrink-0 transition-transform ${
                        clientSwitcherOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {clientSwitcherOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[9999]"
                        onClick={() => setClientSwitcherOpen(false)}
                      />
                      <div className="absolute left-0 mt-1 w-56 bg-white rounded-lg shadow-lg z-[10000] overflow-hidden">
                        {/* Current client (highlighted) */}
                        <div className="px-4 py-2.5 bg-secondary text-white text-sm font-medium truncate">
                          {activeClientDisplayName}
                        </div>
                        {/* Other accessible clients */}
                        {otherClients.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/${c.slug}`}
                            onClick={() => setClientSwitcherOpen(false)}
                            className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition truncate"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-300 truncate hidden lg:block">
                  {clientInfo?.name}
                </p>
              )}
            </div>
          </div>

          {/* Map selector */}
          {hasMultipleMaps && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setMapSelectorOpen(!mapSelectorOpen)}
                className="flex items-center gap-1.5 lg:gap-2 px-2.5 lg:px-4 py-1.5 lg:py-2 bg-primary-dark rounded-md hover:bg-opacity-80 transition"
              >
                <span className="text-xs lg:text-sm font-medium truncate max-w-[100px] lg:max-w-none">
                  {activeMap?.label || "Select Map"}
                </span>
                <ChevronDown
                  size={14}
                  className={`flex-shrink-0 transition-transform ${
                    mapSelectorOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown */}
              {mapSelectorOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-[9999]"
                    onClick={() => setMapSelectorOpen(false)}
                  />

                  {/* Mobile dropdown: fixed, positioned dynamically below header */}
                  <div
                    className="lg:hidden fixed left-3 right-3 z-[10000] bg-white rounded-lg shadow-lg overflow-hidden"
                    style={{ top: mobileDropdownTop }}
                  >
                    {renderMapOptions()}
                  </div>

                  {/* Desktop dropdown: absolute right-aligned */}
                  <div className="hidden lg:block absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-[10000] overflow-hidden">
                    {renderMapOptions()}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Single map badge */}
          {!hasMultipleMaps && activeMap && (
            <div className="px-2 lg:px-3 py-1 bg-primary-dark rounded text-xs lg:text-sm truncate">
              {activeMap.label}
            </div>
          )}

          {/* Desktop-only: Competitors toggle */}
          {showCompetitorsToggle && (
            <button
              onClick={handleToggleCompetitors}
              className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition flex-shrink-0 ${
                competitorsVisible
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : "bg-primary-dark text-gray-300 hover:text-white hover:bg-opacity-80"
              }`}
              title={
                competitorsVisible ? "Hide competitors" : "Show competitors"
              }
            >
              {competitorsLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Users size={14} />
              )}
              <span>
                {competitorsVisible ? "Competitors On" : "Competitors"}
              </span>
            </button>
          )}

          {/* Desktop-only: Opportunities toggle */}
          {showOpportunitiesToggle && (
            <button
              onClick={handleToggleOpportunities}
              className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition flex-shrink-0 ${
                opportunitiesVisible
                  ? "bg-purple-700 text-white hover:bg-purple-800"
                  : "bg-primary-dark text-gray-300 hover:text-white hover:bg-opacity-80"
              }`}
              title={
                opportunitiesVisible
                  ? "Hide opportunities"
                  : "Show opportunities"
              }
            >
              {opportunitiesLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Briefcase size={14} />
              )}
              <span>
                {opportunitiesVisible ? "Opportunities On" : "Opportunities"}
              </span>
            </button>
          )}

          {/* Right side: nav links (desktop only) + user menu (always) */}
          <div className="flex items-center gap-0.5 lg:gap-2 flex-shrink-0">
            {/* Reload button: desktop only (on mobile, it's in the user menu) */}
            <button
              onClick={handleReload}
              className="hidden lg:flex p-2 text-gray-300 hover:text-white transition rounded-md hover:bg-primary-dark"
              title="Reload data"
            >
              <RefreshCw size={18} />
            </button>

            {/* Desktop-only: Reports link */}
            {reportsEnabled && clientSlug && (
              <Link
                to={`/${clientSlug}/reports`}
                className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-sm text-gray-300 hover:text-white transition"
                title="Reports"
              >
                <FileText size={16} />
                <span>Reports</span>
              </Link>
            )}

            {/* Desktop-only: FAQs link */}
            {hasFaq && clientSlug && (
              <Link
                to={`/${clientSlug}/faq`}
                className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-sm text-gray-300 hover:text-white transition"
                title="FAQs"
              >
                <HelpCircle size={16} />
                <span>FAQs</span>
              </Link>
            )}

            {/* User menu: always visible */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-md hover:bg-primary-dark transition"
              >
                <div className="w-7 h-7 lg:w-8 lg:h-8 bg-secondary rounded-full flex items-center justify-center">
                  <User size={14} />
                </div>
                <ChevronDown
                  size={14}
                  className={`hidden lg:block transition-transform ${
                    userMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[9999]"
                    onClick={() => setUserMenuOpen(false)}
                  />

                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-[10000] overflow-hidden">
                    {/* User info */}
                    <div className="px-4 py-3 border-b">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user?.displayName || user?.email}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user?.email}
                      </p>
                      {isAdmin && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-secondary text-white rounded">
                          Admin
                        </span>
                      )}
                    </div>

                    {/* Reload: visible in mobile user menu */}
                    <button
                      onClick={() => {
                        handleReload();
                        setUserMenuOpen(false);
                      }}
                      className="lg:hidden flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                    >
                      <RefreshCw size={16} />
                      Reload Data
                    </button>

                    {/* Client switcher: mobile + desktop user menu */}
                    {hasMultipleClients && otherClients.length > 0 && (
                      <div className="border-t">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Switch Client
                        </div>
                        {otherClients.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/${c.slug}`}
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <MapPin
                              size={14}
                              className="text-gray-400 flex-shrink-0"
                            />
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Admin link */}
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                      >
                        <Settings size={16} />
                        Admin Dashboard
                      </Link>
                    )}

                    {/* Sign out */}
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition border-t"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ============================================
            MOBILE ACTION BAR (second row)
            Feature toggles + nav links on their own row
            ============================================ */}
        {showMobileActionBar && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10 lg:hidden overflow-x-auto hide-scrollbar">
            {/* Competitors toggle */}
            {showCompetitorsToggle && (
              <button
                onClick={handleToggleCompetitors}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition whitespace-nowrap flex-shrink-0 ${
                  competitorsVisible
                    ? "bg-teal-600 text-white active:bg-teal-700"
                    : "bg-primary-dark text-gray-300 active:text-white"
                }`}
              >
                {competitorsLoading ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Users size={13} />
                )}
                <span>Competitors</span>
              </button>
            )}

            {/* Opportunities toggle */}
            {showOpportunitiesToggle && (
              <button
                onClick={handleToggleOpportunities}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition whitespace-nowrap flex-shrink-0 ${
                  opportunitiesVisible
                    ? "bg-purple-700 text-white active:bg-purple-800"
                    : "bg-primary-dark text-gray-300 active:text-white"
                }`}
              >
                {opportunitiesLoading ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Briefcase size={13} />
                )}
                <span>Opportunities</span>
              </button>
            )}

            {/* Reports link */}
            {reportsEnabled && clientSlug && (
              <Link
                to={`/${clientSlug}/reports`}
                className="flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white active:text-white transition flex-shrink-0"
                title="Reports"
              >
                <FileText size={14} />
              </Link>
            )}

            {/* FAQ link */}
            {hasFaq && clientSlug && (
              <Link
                to={`/${clientSlug}/faq`}
                className="flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white active:text-white transition flex-shrink-0"
                title="FAQ"
              >
                <HelpCircle size={14} />
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
